import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { getCourseReviews } from "@/features/reviews/api";
import { useGetAllCourses } from "@/features/courses/hooks/useGetAllCourses";

// There is no /reviews/featured/ endpoint on the API — it 404s — so the rail
// is assembled here instead, out of the per-course reviews the course pages
// already use. Kept to a handful of courses: every slug here is one more
// request on a page an anonymous visitor sees first.
const SOURCE_COURSES = 4;
const PER_COURSE = 3;
const MAX_REVIEWS = 12;

const MIN_STARS = 4;
// Long enough to say something, short enough not to blow the card out of the
// rail's fixed height.
const MIN_LENGTH = 40;
const MAX_LENGTH = 260;

/**
 * Testimonials for the landing page marquee.
 *
 * Reviews cluster on the courses people actually take, so the best-liked
 * courses are the ones worth asking. Each query is keyed exactly as
 * useCourseReviews keys it, so a visitor who opens one of these course pages
 * afterwards reads it straight from the cache.
 */
export function useFeaturedReviews() {
	const { courses = [] } = useGetAllCourses();

	// The course rides along with its reviews: a testimonial that does not say
	// what it is about is just a nice sentence.
	const sources = useMemo(
		() =>
			[...courses]
				.sort((a, b) => (b?.like_count ?? 0) - (a?.like_count ?? 0))
				.slice(0, SOURCE_COURSES)
				.filter((course) => course?.slug)
				.map((course) => ({
					slug: course.slug,
					name: course.name,
					english_name: course.english_name ?? null,
				})),
		[courses]
	);

	const results = useQueries({
		queries: sources.map(({ slug }) => ({
			queryKey: ["reviews", slug],
			queryFn: () => getCourseReviews({ course: { slug } }),
			// A course without reviews is an ordinary outcome, not a failure to
			// retry — and nothing on the page depends on this succeeding.
			retry: false,
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		})),
	});

	const isPending = results.some((result) => result.isPending);

	// Keyed on when each query last settled rather than on `results`, which is
	// a fresh array on every render.
	const settled = results
		.map(
			(result, index) => `${sources[index]?.slug}:${result.dataUpdatedAt}`
		)
		.join(",");

	const reviews = useMemo(() => {
		const seen = new Set();
		const collected = [];

		const perCourse = results.map((result, index) => {
			const course = sources[index];
			const usable = (result.data?.reviews ?? [])
				.filter((review) => {
					const content = review?.content?.trim() ?? "";
					return (
						content.length >= MIN_LENGTH &&
						content.length <= MAX_LENGTH &&
						(review?.stars ?? 0) >= MIN_STARS &&
						review?.uuid &&
						!seen.has(review.uuid)
					);
				})
				.sort((a, b) => (b?.stars ?? 0) - (a?.stars ?? 0))
				// Capped per course so one busy course cannot fill the rail on
				// its own.
				.slice(0, PER_COURSE);

			for (const review of usable) seen.add(review.uuid);

			return usable.map((review) => ({ ...review, course }));
		});

		// Taken a course at a time, the rail would run three cards about the
		// same course in a row — now that each card names its course, that
		// reads as one course with three fans. Dealt round-robin instead.
		for (let i = 0; i < PER_COURSE; i += 1) {
			for (const reviews of perCourse) {
				if (reviews[i]) collected.push(reviews[i]);
			}
		}

		return collected.slice(0, MAX_REVIEWS);
	}, [settled]);

	return { reviews: reviews.length > 0 ? reviews : null, isPending };
}
